import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, User, Wallet, PiggyBank, Target, 
  TrendingUp, Users, Crown, Settings, LogOut, Menu, X,
  ChevronLeft, BarChart3, Microscope, Calculator, Award, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { cn } from '../lib/utils';

const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Mon Profil', href: '/profile', icon: User },
  { name: 'CAP Score', href: '/cap-score', icon: Award },
  { name: 'Gestion Financière', href: '/finances', icon: PiggyBank },
  { name: 'Patrimoine', href: '/patrimoine', icon: Wallet },
  { name: 'Objectifs', href: '/objectives', icon: Target },
  { name: 'Marchés', href: '/markets', icon: TrendingUp },
  { name: 'Analyser', href: '/analyse', icon: Microscope },
  { name: 'Simulateurs', href: '/outils', icon: Calculator },
  { name: 'Super-Investisseurs', href: '/super-investors', icon: Crown },
  { name: 'Communauté', href: '/community', icon: Users },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  // Append the Admin link only when the current user has elevated privileges.
  const navigation = user?.is_admin
    ? [...baseNavigation, { name: 'Administration', href: '/admin', icon: Shield }]
    : baseNavigation;
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const levelNames = [
    'Apprenti', 'Investisseur', 'Analyste', 'Expert', 'Maître'
  ];

  const getLevelName = (level) => levelNames[Math.min(level - 1, levelNames.length - 1)] || 'Novice';

  return (
    <div className="min-h-screen bg-cap-bg flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-cap-bg-subtle border-r border-white/5 transition-all duration-300",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        collapsed ? "w-20" : "w-64"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
          {!collapsed && (
            <h1 className="font-display text-xl font-bold text-white">
              CAP <span className="text-cap-emerald">OS</span>
            </h1>
          )}
          {collapsed && (
            <span className="font-display text-xl font-bold text-cap-emerald mx-auto">C</span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-white/5 text-cap-text-secondary"
          >
            <ChevronLeft className={cn("w-5 h-5 transition-transform", collapsed && "rotate-180")} />
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-cap-text-secondary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-3 py-3 rounded-lg transition-all group",
                  isActive 
                    ? "bg-cap-emerald/10 text-cap-emerald" 
                    : "text-cap-text-secondary hover:text-white hover:bg-white/5"
                )}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className={cn("w-5 h-5 flex-shrink-0", collapsed && "mx-auto")} />
                {!collapsed && <span className="font-medium">{item.name}</span>}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-white/5">
          {!collapsed ? (
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-cap-bg-elevated">
              <div className="w-10 h-10 rounded-full bg-cap-emerald/20 flex items-center justify-center">
                <span className="font-display font-bold text-cap-emerald">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.username}</p>
                <p className="text-cap-text-muted text-xs">
                  {getLevelName(user?.level)} • {user?.xp} XP
                </p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10 rounded-full bg-cap-emerald/20 flex items-center justify-center">
                <span className="font-display font-bold text-cap-emerald">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          )}
          
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={cn(
              "w-full text-cap-text-secondary hover:text-cap-error hover:bg-cap-error/10",
              collapsed && "px-0"
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="ml-2">Déconnexion</span>}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden h-16 flex items-center justify-between px-4 border-b border-white/5 bg-cap-bg-subtle">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/5 text-cap-text-secondary"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-display text-lg font-bold text-white">
            CAP <span className="text-cap-emerald">OS</span>
          </h1>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
